<template>
  <section class="admin-page">
    <div class="admin-scroll">
      <header class="admin-header">
        <div>
          <span class="admin-label">评论管理</span>
          <h2>管理博客评论</h2>
          <p class="admin-desc">查看和管理所有文章的评论，删除不当内容。</p>
        </div>
      </header>

      <section class="admin-panel">
        <div class="admin-toolbar">
          <label class="admin-search">
            <svg class="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" class="admin-search-input" placeholder="搜索评论内容、用户名" v-model="searchKeyword" @keyup.enter="handleSearch" />
          </label>
        </div>

        <div class="admin-list">
          <div v-if="loading" class="admin-loading">
            <div class="admin-spinner"></div>
            <span>加载中...</span>
          </div>
          <article
            v-for="comment in comments"
            :key="comment.id"
            class="admin-list-item"
          >
            <div class="admin-list-body">
              <div class="comment-item">
                <img v-if="comment.avatar" :src="comment.avatar" class="comment-avatar" :alt="comment.username" />
                <span v-else class="comment-avatar-placeholder">{{ comment.username?.charAt(0)?.toUpperCase() }}</span>
                <div class="comment-content">
                  <div class="comment-meta">
                    <span class="comment-username">{{ comment.username }}</span>
                    <span class="comment-time">{{ formatDate(comment.created_at) }}</span>
                  </div>
                  <p class="comment-text">{{ truncateText(comment.content, 100) }}</p>
                  <span class="comment-article">文章：{{ comment.article_title || '未知文章' }}</span>
                </div>
              </div>
            </div>
            <div class="admin-list-actions">
              <button class="admin-btn admin-btn-ghost" type="button" style="color: var(--danger);" @click="handleDeleteComment(comment.id)">删除</button>
            </div>
          </article>

          <div v-if="comments.length === 0" class="admin-empty">
            <h3>暂无评论</h3>
            <p>当前没有评论数据。</p>
          </div>
        </div>

        <AdminPagination
          :current-page="currentPage"
          :total-pages="totalPages"
          :on-page-change="goToPage"
        />
      </section>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getAdminComments, deleteComment as deleteCommentApi } from '@/services/commentService'
import { useNotify } from '@/composables/useNotify'
import AdminPagination from '../components/admin/AdminPagination.vue'

const { notify, confirmDialog } = useNotify()

const comments = ref([])
const loading = ref(true)
const searchKeyword = ref('')
const currentPage = ref(1)
const totalPages = ref(1)
const totalComments = ref(0)
const PER_PAGE = 10

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const truncateText = (text, maxLength) => {
  if (!text) return ''
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
}

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchComments()
}

const fetchComments = async () => {
  loading.value = true
  try {
    const keyword = searchKeyword.value.trim()
    const res = await getAdminComments(currentPage.value, PER_PAGE, keyword)
    const data = res.data
    comments.value = data?.list || []
    totalComments.value = data?.total || 0
    totalPages.value = data?.total_page || Math.ceil(totalComments.value / PER_PAGE) || 1
  } catch (e) {
    notify.error(e.message || '获取评论列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  currentPage.value = 1
  fetchComments()
}

const handleDeleteComment = async (id) => {
  if (!await confirmDialog('确定要删除这条评论吗？删除后将无法恢复。')) return
  try {
    await deleteCommentApi(id)
    await fetchComments()
    notify.success('评论删除成功')
  } catch (e) {
    notify.error(`删除失败：${e.message}`)
  }
}

onMounted(() => {
  fetchComments()
})
</script>

<style scoped>
.comment-item {
  display: flex;
  gap: 12px;
  flex: 1;
}

.comment-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.comment-avatar-placeholder {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.comment-content {
  flex: 1;
  min-width: 0;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.comment-username {
  font-weight: 600;
  font-size: 14px;
  color: var(--text-main);
}

.comment-time {
  font-size: 12px;
  color: var(--text-muted);
}

.comment-text {
  margin: 0 0 6px 0;
  font-size: 14px;
  color: var(--text-main);
  line-height: 1.5;
}

.comment-article {
  font-size: 12px;
  color: var(--text-subtle);
}
</style>
